import { PortableTextBlock } from '@portabletext/react';
import clsx from 'clsx';
import { useContext } from 'react';
import { twMerge } from 'tailwind-merge';

import { Container, Section } from 'components/GridV2';
import { LocaleContext } from 'components/rebrand/Layout';

import { Accordion } from './Accordion';
import { Button } from './Button';
import CustomPortableText from './CustomPortableText';
import { H2 } from './Typography';

const isStringArr = (value: unknown): value is string[] => Array.isArray(value) && typeof value[0] === 'string';

type Props = {
  className?: string;
  faqItems: {
    answer: string[] | PortableTextBlock | PortableTextBlock[];
    question: string;
  }[];
  heading: string | null;
  hideHeading?: boolean;
  hideStillHaveQuestions?: boolean;
  subheading: PortableTextBlock | PortableTextBlock[] | null;
};

export const FAQ = ({
  className,
  faqItems,
  heading,
  hideHeading = false,
  hideStillHaveQuestions = false,
  subheading,
}: Props) => {
  return (
    <Section>
      <Container className={twMerge('px-32 pb-24 xs:px-0 md:px-144 md:pb-64', className)}>
        {!hideHeading && (
          <div className="mx-auto mb-32 text-center md:mb-64">
            <H2 className="text-primary-400">{heading ?? 'FAQS'}</H2>
            {subheading && <span className="mt-20 text-xl leading-normal"></span>}
          </div>
        )}
        {faqItems.map(({ answer, question }, i) => (
          <div
            key={question}
            className={twMerge(
              clsx('py-24 text-md text-grey-400', i !== faqItems.length - 1 && 'border-b border-grey-75'),
            )}
          >
            <Accordion
              className="[&_p]:pb-12 [&_p]:text-md [&_svg]:text-primary-300"
              title={<span className="text-lg font-medium text-grey-500">{question}</span>}
            >
              {isStringArr(answer) ? (
                answer.map((str, i) => (
                  <p
                    key={i}
                    className="[&_a]:text-primary-300 [&_li]:list-inside [&_ul]:list-disc [&_ul]:pl-16"
                    dangerouslySetInnerHTML={{ __html: str }}
                  />
                ))
              ) : (
                <CustomPortableText content={answer} />
              )}
            </Accordion>
          </div>
        ))}
      </Container>

      {!hideStillHaveQuestions && (
        <Container className="px-32 xs:px-0">
          <StillHaveQuestions />
        </Container>
      )}
    </Section>
  );
};

export const StillHaveQuestions = () => {
  const locale = useContext(LocaleContext);
  return (
    <div className="flex flex-col rounded-24 bg-grey-50 px-30 pb-40 pt-32 md:px-144">
      <h3 className="mb-8 text-center text-xl font-medium">
        {{ en: 'Still have questions?', fr: 'Toujours des questions?' }[locale]}
      </h3>
      <span className="mb-32 text-center text-lg">
        {
          {
            en: "Can't find the answer you're looking for? Please chat to our friendly team.",
            fr: "Vous ne trouvez pas la réponse que vous cherchez ? N'hésitez pas à contacter notre équipe.",
          }[locale]
        }
      </span>
      <a
        className="mx-auto w-fit"
        href={`https://help.koho.ca/${locale === 'fr' ? 'fr' : ''}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Button>{{ en: 'Visit our FAQs', fr: 'Consulter nos FAQs' }[locale]}</Button>
      </a>
    </div>
  );
};
